<template>
  <div class="layer-widget-panel scrollbar">
    <div class="measurements-container">
      <div class="measurements">
        <div class="form-group hidden position">
          <div class="form-row">
            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">X</span>
              </div>
              <input
                type="number"
                class="form-control"
                id="layer-x"
                v-model="xPosition"
                :disabled="isWidgetLocked"
              />
            </div>

            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">Y</span>
              </div>
              <input
                type="number"
                class="form-control"
                v-model="yPosition"
                :disabled="isWidgetLocked"
              />
            </div>
          </div>
        </div>

        <div class="form-group hidden size">
          <div class="form-row">
            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">W</span>
              </div>
              <input
                type="number"
                class="form-control"
                id="layer-width"
                v-model="width"
                :disabled="isWidgetLocked"
              />
            </div>

            <div class="input-group col">
              <div class="input-label">
                <span class="input-group-text">H</span>
              </div>
              <input
                type="number"
                class="form-control"
                v-model="height"
                :disabled="isWidgetLocked"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="extra-icons">
        <LockIcon v-show="isWidgetLocked" :locked="isWidgetLocked" />
        <VisibilityIcon :isVisible="isWidgetVisible" @clicked="updateVisibility" />
        <PixelIcon :usePixels="usePixels" @clicked="changeValuesMode" />
      </div>
    </div>

    <template>
      <div v-if="showLocationPicker" class="location-container">
        <LocationPicker @updateLocation="updateLocation" />

        <button
          v-show="showLocationPicker"
          type="button"
          class="button-2-secondary"
          @click="showLocationPicker = false"
        >
          Cancel
        </button>
      </div>

      <template v-if="!showLocationPicker && value.object.weather.location">
        <div class="form-group settings-input mt-3 mb-0" style="border-bottom: 0">
          <label for="template-name">Location</label>
          <input
            class="form-control"
            type="text"
            id="template-name"
            placeholder="Name"
            maxlength="30"
            v-model="value.object.weather.location.label"
          />
          <div
            v-show="value.object.weather.location"
            class="remove-button"
            @click="showLocationPicker = true"
          >
            Change Location
          </div>
        </div>

        <div class="form-group template-input">
          <label for="template-name">Widget Name</label>
          <input
            class="form-control"
            type="text"
            id="template-name"
            placeholder="Name"
            v-model="value.object.weather.name"
          />
        </div>

        <div class="form-group layout-input content-type-input">
          <label>Widget Type</label>
          <select
            class="form-control"
            id="content-mode"
            v-model="weatherType"
            :disabled="isWidgetLockedByOverride"
          >
            <option :value="WEATHER_DISPLAY_TYPE?.NOW.value">
              {{ WEATHER_DISPLAY_TYPE?.NOW.label }}
            </option>
            <option :value="WEATHER_DISPLAY_TYPE?.HOURS.value">
              {{ WEATHER_DISPLAY_TYPE?.HOURS.label }}
            </option>
            <option :value="WEATHER_DISPLAY_TYPE?.DAYS.value">
              {{ WEATHER_DISPLAY_TYPE?.DAYS.label }}
            </option>
          </select>
        </div>

        <div class="form-group template-input">
          <label class="font-poppins t-sm" for="language">Language</label>
          <select
            class="form-control input-underline"
            id="language"
            v-model="value.object.weather.language"
          >
            <option :value="WEATHER_LANGUAGES.ENGLISH.value">
              {{ WEATHER_LANGUAGES.ENGLISH.label }}
            </option>
            <option :value="WEATHER_LANGUAGES.SWEDISH.value">
              {{ WEATHER_LANGUAGES.SWEDISH.label }}
            </option>
          </select>
        </div>

        <div class="font-color-container">
          <div class="font-container form-group layout-input">
            <label for="template-name">Font</label>
            <FontSelector
              :disabled="isWidgetLockedByOverride"
              :value="value.object.weather.textFont"
              @update:value="updateWeatherFont"
            ></FontSelector>
          </div>

          <div class="color-section">
            <div class="color-buttons-container">
              <ColorPicker v-model="value.object.weather.fontColor" :leftPosition="-166">
                <div class="color-button" title="Font Color">
                  <i class="material-icons-outlined align-middle">text_fields</i>
                  <div
                    class="color-bar"
                    :style="{ backgroundColor: value.object.weather.fontColor }"
                  ></div>
                </div>
              </ColorPicker>

              <ColorPicker v-model="value.object.weather.backgroundColor" :leftPosition="-216">
                <div class="color-button" title="Background Color">
                  <img class="logo w-165" src="@/assets/icon/Colors.svg" alt="Background Color" />
                  <div
                    class="color-bar"
                    :style="{ backgroundColor: value.object.weather.backgroundColor }"
                  ></div>
                </div>
              </ColorPicker>

              <ColorPicker v-model="value.object.weather.iconColor" :leftPosition="-266">
                <div class="color-button" title="Icon Color">
                  <i class="material-icons-outlined align-middle">light_mode</i>
                  <div
                    class="color-bar"
                    :style="{ backgroundColor: value.object.weather.iconColor }"
                  ></div>
                </div>
              </ColorPicker>
            </div>
          </div>
        </div>

        <div class="form-group template-input">
          <label class="font-poppins t-sm" for="temperature">Temperature</label>
          <select
            class="form-control input-underline"
            id="temperature"
            v-model="value.object.weather.temperatureUnit"
          >
            <option :value="WEATHER_TEMPERATURE.CELSIUS.value">
              {{ WEATHER_TEMPERATURE.CELSIUS.label }}
            </option>
            <option :value="WEATHER_TEMPERATURE.FAHRENHEIT.value">
              {{ WEATHER_TEMPERATURE.FAHRENHEIT.label }}
            </option>
          </select>
        </div>

        <div
          v-if="value.object.weather.type === WEATHER_DISPLAY_TYPE.DAYS.value"
          class="form-group template-input"
        >
          <label class="font-poppins t-sm" for="time-format">Weekday Format</label>
          <select
            class="form-control input-underline"
            id="time-format"
            v-model="value.object.weather.dayFormat"
          >
            <option :value="WEATHER_DAY_FORMAT.SHORT.value">
              {{ WEATHER_DAY_FORMAT.SHORT.label }}
            </option>
            <option :value="WEATHER_DAY_FORMAT.WORD.value">
              {{ WEATHER_DAY_FORMAT.WORD.label }}
            </option>
            <option :value="WEATHER_DAY_FORMAT.DAY_OF_MONTH.value">
              {{ WEATHER_DAY_FORMAT.DAY_OF_MONTH.label }}
            </option>
          </select>
        </div>

        <div
          v-if="value.object.weather.type === WEATHER_DISPLAY_TYPE.HOURS.value"
          class="form-group template-input"
        >
          <label class="font-poppins t-sm" for="time-format">Time Format</label>

          <select
            class="form-control input-underline"
            id="time-format"
            v-model="value.object.weather.timeFormat"
          >
            <option :value="WEATHER_HOURS_FORMAT.TWELVE.value">
              {{ WEATHER_HOURS_FORMAT.TWELVE.label }}
            </option>

            <option :value="WEATHER_HOURS_FORMAT.TWENTY_FOUR.value">
              {{ WEATHER_HOURS_FORMAT.TWENTY_FOUR.label }}
            </option>
          </select>
        </div>

        <div class="location-text">
          <div class="floor-switch">
            <div class="custom-control custom-switch check">
              <input
                type="checkbox"
                class="custom-control-input"
                id="location-text"
                v-model="value.object.weather.showLocationText"
              />
              <label class="custom-control-label" for="location-text"></label>
            </div>
          </div>

          <BaseText variant="inputLabel">Show Location</BaseText>
        </div>

        <div
          v-if="
            value.object.weather?.type === WEATHER_DISPLAY_TYPE.NOW.value &&
            !value.object.weather.showLocationText
          "
          class="form-group form-range layout-input"
        >
          <div class="slider-top">
            <div class="form-label">Space in Between</div>
            <div class="value">
              {{ value.object.weather.widthSeparationPercentage || 100 }}
            </div>
          </div>
          <div class="slider-container">
            <BaseSlider
              :value="value.object.weather.widthSeparationPercentage || 100"
              :min="1"
              :max="100"
              @update:value="updateNowSpaceSeparation"
            />
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
  import { Sketch } from 'vue-color';
  import ClickOutside from 'vue-click-outside';

  import { restrictToBounds } from '@/helpers/draggable';

  import ColorPicker from '@/components/ColorPicker.vue';
  import PixelIcon from '@/components/layoutDesigner/editor/settingPanel/PixelIcon.vue';
  import LockIcon from '@/components/layoutDesigner/editor/settingPanel/LockIcon.vue';
  import VisibilityIcon from '@/components/layoutDesigner/editor/settingPanel/VisibilityIcon.vue';
  import FontSelector from '@/components/common/fontSelector/FontSelector.vue';
  import LocationPicker from '@/components/apps/weather/LocationPicker.vue';
  import BaseText from '@/components/common/ui/atoms/baseText/BaseText.vue';
  import { BaseSlider } from '@ui/atoms/baseSlider';

  import {
    WEATHER_DISPLAY_TYPE,
    WEATHER_TEMPERATURE,
    WEATHER_HOURS_FORMAT,
    WEATHER_DAY_FORMAT,
    WEATHER_LANGUAGES,
  } from '@/constant/weatherApp';

  import widgetMixin from '@/components/templates/editor/settingPanel/widgetMixin';

  export default {
    name: 'WeatherWidgetPanel',

    mixins: [widgetMixin],

    directives: { ClickOutside },

    components: {
      ColorPicker,
      PixelIcon,
      LockIcon,
      VisibilityIcon,
      FontSelector,
      LocationPicker,
      Sketch,
      BaseSlider,
      BaseText,
    },

    data() {
      return {
        WEATHER_DISPLAY_TYPE,
        WEATHER_TEMPERATURE,
        WEATHER_HOURS_FORMAT,
        WEATHER_DAY_FORMAT,
        WEATHER_LANGUAGES,
        showLocationPicker: false,
      };
    },

    created() {
      if (
        this.value.object.item_type === 'app/weather' &&
        this.value.object.weather &&
        ![true, false].includes(this.value.object.weather.showLocationText)
      ) {
        this.$set(this.value.object.weather, 'showLocationText', true);
      }
    },

    computed: {
      // ------ WEATHER APP -------- //

      isWeatherTypeNow() {
        return this.value.object.weather?.type === WEATHER_DISPLAY_TYPE.NOW.value;
      },

      weatherType: {
        get() {
          return this.value.object.weather?.type;
        },
        set(value) {
          this.$set(this.value.object.weather, 'type', value);
        },
      },

      weatherTextSize: {
        get() {
          return this.isWeatherTypeNow
            ? this.value.object.weather.simpleTextSizeSimple
            : this.value.object.weather.multipleTextSize;
        },
        set(value) {
          this.$set(
            this.value.object.weather,
            this.isWeatherTypeNow ? 'simpleTextSizeSimple' : 'multipleTextSize',
            restrictToBounds(Number(value), 8, 120),
          );
        },
      },

      weatherNumberSize: {
        get() {
          return this.isWeatherTypeNow
            ? this.value.object.weather.simpleNumberSize
            : this.value.object.weather.multipleNumberSize;
        },
        set(value) {
          this.$set(
            this.value.object.weather,
            this.isWeatherTypeNow ? 'simpleNumberSize' : 'multipleNumberSize',
            restrictToBounds(Number(value), 6, 120),
          );
        },
      },
    },

    methods: {
      updateLocation(data) {
        this.$set(this.value.object.weather, 'location', data);
        this.showLocationPicker = false;

        if (!this.value.object.weather.name) {
          this.$set(this.value.object.weather, 'name', data.name);
        }
      },

      updateWeatherFont(newFont) {
        this.$set(this.value.object.weather, 'textFont', newFont);
      },

      updateNowSpaceSeparation(value) {
        this.$set(
          this.value.object.weather,
          'widthSeparationPercentage',
          restrictToBounds(Number(value), 1, 100),
        );
      },

      limitOpacity(value, key, defaultValue) {
        if (!value & (value !== 0)) {
          this.$set(this.value.object, key, defaultValue);
        }

        if (value > 100) {
          this.$set(this.value.object, key, 100);
        }

        if (value < 0) {
          this.$set(this.value.object, key, 0);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .color-buttons-container {
    display: flex;
    position: relative;
    align-items: flex-end;
    gap: 10px;
    min-width: 140px;
  }

  .layer-widget-panel {
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    margin-right: 0px;
    padding-right: 10px;
  }

  .measurements-container {
    padding-top: 6px;
  }

  .sub-section-title {
    margin-top: 8px;
  }

  .alignment {
    margin-top: 6px;
  }

  .alignments {
    display: flex;
    gap: 8px;

    .alignment-mode {
      width: 26px;
      margin: 0 2px;

      input:checked + label {
        color: $primaryText;
        background: #f5f5f5;
        padding: 16px;
      }

      input + label {
        width: 30px;
        height: 30px;
        border-radius: 4px;
        color: $secondaryText;
      }

      .alignment-label {
        cursor: pointer;
      }
    }
  }

  .form-group {
    .row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      margin: 0;
    }
  }

  .font-color-container {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 1rem;

    .form-group {
      margin-bottom: 0;
      width: 100%;
    }

    .color-section {
      display: flex;
      position: relative;
      align-items: flex-end;
      gap: 10px;
      min-width: 140px;
    }
  }

  .color-button {
    background-color: $lineGrey;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;
    height: 40px;
    padding: 8px 6px 6px 6px;
    width: 40px;

    i {
      color: $tableText;
      font-size: 18px;
      height: 18px;
      width: 18px;
    }

    img {
      margin-top: -1px;
      height: 20px;
      width: 20px;
    }

    .color-bar {
      border-radius: 5px;
      height: 4px;
      width: 100%;
      background-color: black;
    }
  }

  label {
    margin-bottom: 4px;
  }

  .location-container {
    margin-top: 16px;
    width: 100%;
  }

  .remove-button {
    width: 100%;
    text-align: right;

    &:hover {
      cursor: pointer;
      color: $primaryRed;
    }
  }

  .location-text {
    display: flex;
    margin-bottom: 8px;
  }

  .slider-container {
    width: 100%;
    padding-right: 24px;
  }

  .form-range {
    display: flex;
    gap: 16px;
    flex-direction: column;
    margin-top: 20px;

    &.form-group {
      margin-bottom: 20px;
    }
  }
</style>
